<script lang="ts">
	import { featuredMediaUrl } from "#lib/shared/media.js";
	import { formatCurrencyRound } from "#lib/shared/formatters.js";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import { PropertyCard, PropertyCta, ListingRow } from "#lib/web-ui/fergmar-com/index.js";
	import BedDoubleIcon from "@lucide/svelte/icons/bed-double";
	import BathIcon from "@lucide/svelte/icons/bath";
	import HomeIcon from "@lucide/svelte/icons/home";
	import TreesIcon from "@lucide/svelte/icons/trees";
	import LandPlotIcon from "@lucide/svelte/icons/land-plot";
	import type { RouterOutputs } from "#trpc/router.js";
	import type { OnMarketUnitEntity } from "#backend/modules/buildium-listings/buildium-listings.schemas.js";

	type PublicListing = RouterOutputs["public"]["investmentListings"]["getActiveListings"][number];

	let { data } = $props();
</script>

<svelte:head>
	<title>Properties | Fergmar</title>
	<meta
		name="description"
		content="Browse Fergmar properties for sale, for rent, and for lease in the Victoria, TX crossroads area."
	/>
</svelte:head>

<PropertyCta />

<!-- Homes for Sale -->
<ListingRow
	title="Homes for Sale"
	subtitle="Residential properties available for purchase"
	viewMoreHref="/properties/for-sale?type=Residential"
	viewMoreLabel="View all homes"
	listingCount={data.counts.homesForSale}
>
	{#snippet cards()}
		{#each data.homesForSale as listing (listing.id)}
			{@const imageUrl = cloudinaryFetch(featuredMediaUrl(listing.media, "cardUrl"), {
				width: 400,
				height: 300,
				crop: "fill"
			})}
			<PropertyCard
				href="/properties/for-sale/{listing.slug}"
				{imageUrl}
				imageAlt="{listing.addressStreet1} {listing.addressCity}, {listing.addressState}"
			>
				{#snippet badge()}
					<span
						class="text-foreground inline-block rounded bg-white/90 px-2.5 py-0.5 text-sm font-bold"
					>
						{listing.currentPrice
							? formatCurrencyRound(listing.currentPrice)
							: "Contact for Price"}
					</span>
				{/snippet}
				{#snippet details()}
					{@render salesDetails(listing)}
				{/snippet}
			</PropertyCard>
		{/each}
	{/snippet}
</ListingRow>

<!-- Apartments for Rent -->
<ListingRow
	title="Apartments for Rent"
	subtitle="Apartments, townhomes, and houses available for rent"
	viewMoreHref="/properties/for-rent?type=MultiFamily"
	viewMoreLabel="View all rentals"
	listingCount={data.counts.apartmentsForRent}
>
	{#snippet cards()}
		{#each data.apartmentsForRent as listing (listing.unit.id)}
			<PropertyCard
				href="/properties/for-rent/{listing.listing.slug}"
				imageUrl={cloudinaryFetch(listing.unit.files?.[0]?.url, {
					width: 400,
					height: 300,
					crop: "fill"
				})}
				imageAlt="{listing.unit.address.addressLine1} {listing.unit.address.city}, {listing
					.unit.address.state}"
			>
				{#snippet badge()}
					<span
						class="text-foreground inline-block rounded bg-white/90 px-2.5 py-0.5 text-sm font-bold"
					>
						{formatCurrencyRound(listing.listing.rent)}/mo
					</span>
				{/snippet}
				{#snippet details()}
					{@render rentalDetails(listing)}
				{/snippet}
			</PropertyCard>
		{/each}
	{/snippet}
</ListingRow>

<!-- Investment Properties -->
<ListingRow
	title="Investment Properties"
	subtitle="Single-family, multifamily, and commercial opportunities"
	viewMoreHref="/properties/for-sale?type=Multi-Family%2CCommercial"
	viewMoreLabel="View all investments"
	listingCount={data.counts.investmentForSale}
>
	{#snippet cards()}
		{#each data.investmentForSale as listing (listing.id)}
			{@const imageUrl = cloudinaryFetch(featuredMediaUrl(listing.media, "cardUrl"), {
				width: 400,
				height: 300,
				crop: "fill"
			})}
			<PropertyCard
				href="/properties/for-sale/{listing.slug}"
				{imageUrl}
				imageAlt="{listing.addressStreet1} {listing.addressCity}, {listing.addressState}"
			>
				{#snippet badge()}
					<span
						class="text-foreground inline-block rounded bg-white/90 px-2.5 py-0.5 text-sm font-bold"
					>
						{listing.currentPrice
							? formatCurrencyRound(listing.currentPrice)
							: "Contact for Price"}
					</span>
				{/snippet}
				{#snippet details()}
					{@render salesDetails(listing)}
				{/snippet}
			</PropertyCard>
		{/each}
	{/snippet}
</ListingRow>

<!-- Commercial for Lease -->
<ListingRow
	title="Commercial for Lease"
	subtitle="Retail, office, and flex spaces for your business"
	viewMoreHref="/properties/for-lease"
	viewMoreLabel="View all leases"
	listingCount={data.counts.commercialForLease}
>
	{#snippet cards()}
		{#each data.commercialForLease as listing (listing.unit.id)}
			<PropertyCard
				href="/properties/for-lease/{listing.listing.slug}"
				imageUrl={cloudinaryFetch(listing.unit.files?.[0]?.url, {
					width: 400,
					height: 300,
					crop: "fill"
				})}
				imageAlt="{listing.unit.address.addressLine1} {listing.unit.address.city}, {listing
					.unit.address.state}"
			>
				{#snippet badge()}
					<span
						class="text-foreground inline-block rounded bg-white/90 px-2.5 py-0.5 text-sm font-bold"
					>
						{formatCurrencyRound(listing.listing.rent)}/mo
					</span>
				{/snippet}
				{#snippet details()}
					{@render rentalDetails(listing)}
				{/snippet}
			</PropertyCard>
		{/each}
	{/snippet}
</ListingRow>

<!-- Land for Sale -->
<ListingRow
	title="Land for Sale"
	subtitle="Acreage, ranch, and development land"
	viewMoreHref="/properties/for-sale?type=Farm+%26+Ranch%2CLand"
	viewMoreLabel="View all land"
	listingCount={data.counts.landForSale}
>
	{#snippet cards()}
		{#each data.landForSale as listing (listing.id)}
			{@const imageUrl = cloudinaryFetch(featuredMediaUrl(listing.media, "cardUrl"), {
				width: 400,
				height: 300,
				crop: "fill"
			})}
			<PropertyCard
				href="/properties/for-sale/{listing.slug}"
				{imageUrl}
				imageAlt="{listing.addressStreet1} {listing.addressCity}, {listing.addressState}"
			>
				{#snippet badge()}
					<span
						class="text-foreground inline-block rounded bg-white/90 px-2.5 py-0.5 text-sm font-bold"
					>
						{listing.currentPrice
							? formatCurrencyRound(listing.currentPrice)
							: "Contact for Price"}
					</span>
				{/snippet}
				{#snippet details()}
					{@render salesDetails(listing)}
				{/snippet}
			</PropertyCard>
		{/each}
	{/snippet}
</ListingRow>

<!-- Shared detail snippets -->

{#snippet salesDetails(listing: PublicListing)}
	<div class="flex flex-col">
		<span class="text-sm font-semibold">{listing.addressStreet1}</span>
		<span class="text-muted-foreground text-xs">
			{listing.addressCity}, {listing.addressState}
			{listing.addressZip}
		</span>
	</div>
	<div class="text-muted-foreground mt-2 flex flex-row gap-3 text-xs">
		{#if listing.bedrooms || listing.totalBathrooms || listing.livingArea || listing.lotSize}
			{#if listing.bedrooms}
				<div class="flex items-center gap-1">
					<BedDoubleIcon class="h-3.5 w-3.5" />
					<span>{listing.bedrooms} Beds</span>
				</div>
			{/if}
			{#if listing.totalBathrooms}
				<div class="flex items-center gap-1">
					<BathIcon class="h-3.5 w-3.5" />
					<span>{listing.totalBathrooms} Baths</span>
				</div>
			{/if}
			{#if listing.livingArea}
				<div class="flex items-center gap-1">
					<HomeIcon class="h-3.5 w-3.5" />
					<span>{listing.livingArea.toLocaleString()} sqft</span>
				</div>
			{:else if listing.lotSize}
				<div class="flex items-center gap-1">
					<TreesIcon class="h-3.5 w-3.5" />
					<span>
						{#if listing.lotSizeUnit === "Acres"}
							{listing.lotSize} Acres
						{:else}
							{(listing.lotSize / 43560).toFixed(2)} Acre(s)
						{/if}
					</span>
				</div>
			{/if}
		{:else}
			<span>Call for details</span>
		{/if}
	</div>
{/snippet}

{#snippet rentalDetails(listing: OnMarketUnitEntity)}
	<div class="flex flex-col">
		<div>
			<span class="text-sm font-semibold">{listing.unit.address.addressLine1}</span>
			{#if listing.unit.address.addressLine2}
				<span class="text-border ml-1">|</span>
				<span class="text-primary ml-1 text-sm">{listing.unit.address.addressLine2}</span>
			{/if}
		</div>
		<span class="text-muted-foreground text-xs">
			{listing.unit.address.city}, {listing.unit.address.state}
			{listing.unit.address.postalCode}
		</span>
	</div>
	<div class="text-muted-foreground mt-2 flex flex-row gap-3 text-xs">
		{#if listing.unit.unitBedrooms?.count}
			<div class="flex items-center gap-1">
				<BedDoubleIcon class="h-3.5 w-3.5" />
				<span
					>{listing.unit.unitBedrooms.count}
					{listing.unit.unitBedrooms.count === 1 ? "Bed" : "Beds"}</span
				>
			</div>
		{/if}
		{#if listing.unit.unitBathrooms?.count}
			<div class="flex items-center gap-1">
				<BathIcon class="h-3.5 w-3.5" />
				<span
					>{listing.unit.unitBathrooms.count}
					{listing.unit.unitBathrooms.count === 1 ? "Bath" : "Baths"}</span
				>
			</div>
		{/if}
		{#if listing.unit.unitSize}
			<div class="flex items-center gap-1">
				<LandPlotIcon class="h-3.5 w-3.5" />
				<span>{listing.unit.unitSize.toLocaleString()} sf</span>
			</div>
		{/if}
	</div>
{/snippet}
